import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureException, captureEvent } from '../../lib/telemetry/posthog/frontend';
import { buildConversationCreationProps } from '../../lib/telemetry/conversation-creation-contract';
import { buildOnboardingProps } from '../../lib/telemetry/onboarding-contract';
import { AppSwitcher } from './AppSwitcher';
import { DropdownMenu } from '../ui';
import { Tooltip } from '../Tooltip';
import { CreateOrgModal } from '../setup/CreateOrgModal';
import { SidebarWorkspaceTree } from './SidebarWorkspaceTree';
import { clearSetupDraft } from '../setupDraft';
import { useOrg } from '../../lib/OrgProvider';
import { usePlanBadge } from '../../lib/usePlanBadge';
import { useTrialStatus } from '../../lib/useTrialStatus';
import { TrialSidebarBanner } from './TrialSidebarBanner';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import {
  IconArrowLeft,
  IconChevronLeft,
  IconSelector,
  IconCheck,
  IconLogout,
  IconArrowsLeftRight,
  IconBug,
  IconMenu2,
  IconHome,
  IconDotsVertical,
  IconSettings,
  IconCreditCard,
} from '@tabler/icons-react';
import { CloseIcon, PlusIcon } from '@/components/icons';
import { clsx } from 'clsx';

interface InterviewSession {
  id: string;
  title: string;
  date: string;
  /** Workspace the session lives in. Null/undefined for legacy rows
   *  that haven't been moved into a workspace yet - those render under
   *  an "Unassigned" pseudo-folder in the tree. */
  brand_id?: string | null;
}

interface CollapsibleSidebarProps {
  recentSessions?: InterviewSession[];
  onSessionClick?: (sessionId: string) => void;
  loading?: boolean;
  fetchError?: boolean;
}

export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  recentSessions = [],
  onSessionClick,
  loading = false,
  fetchError = false,
}) => {
  const { user, signOut } = useSupabase();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrg, userOrgs, setActiveOrg, hasPermission, activeWorkspace } = useOrg();

  /**
   * Permission-gated CTA - sourced from the org's RBAC junction table
   * (`org_role_permissions` × `org_permissions`) via OrgProvider, not a
   * hardcoded role-name list. Adding/removing the grant in the DB flows
   * through automatically.
   */
  const canCreateInterview = hasPermission('interviews:create');

  // Extract session ID from current URL path (e.g., /admin/interviews/123 or /admin/interviews/123/responses/456 -> 123)
  const currentSessionId = location.pathname.match(/^\/admin\/interviews\/([^/]+)/)?.[1];

  // On the settings route the mobile top-bar cog morphs into a back arrow
  // (see handleSettingsToggle) so the route is the source of truth for which
  // icon/action the button shows.
  const isSettingsRoute = location.pathname.startsWith('/admin/settings');
  const isDashboardRoute = location.pathname === '/admin' || location.pathname === '/admin/';

  // Check if we're on mobile (below md breakpoint)
  // Initialize state based on screen width to avoid flash on mobile
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isTextVisible = !isCollapsed;
  const mobileToggleRef = useRef<HTMLButtonElement>(null);

  /** Whether the CreateOrgModal is open, driven by the sidebar dropdown. */
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);

  // Handle responsive behavior - only for resize events
  // Initial state is handled in useState initializers above
  useLayoutEffect(() => {
    const checkIfMobile = () => {
      const mobile = window.innerWidth < 768; // md breakpoint
      setIsMobile(mobile);
    };

    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Lock body scroll when sidebar is open on mobile
  useLayoutEffect(() => {
    if (isMobile && !isCollapsed) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
    return undefined;
  }, [isMobile, isCollapsed]);

  // Handle escape key press on mobile
  useEffect(() => {
    const handleEscKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMobile && !isCollapsed) {
        setIsCollapsed(true);
        mobileToggleRef.current?.focus();
      }
    };

    if (isMobile) {
      window.addEventListener('keydown', handleEscKeyPress);
      return () => {
        window.removeEventListener('keydown', handleEscKeyPress);
      };
    }
    return undefined;
  }, [isMobile, isCollapsed]);

  // Close sidebar on mobile when navigating to a new page
  useEffect(() => {
    if (isMobile && !isCollapsed) {
      setIsCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]); // Close on any URL change; isMobile/isCollapsed intentionally omitted

  const handleToggleCollapse = () => {
    setIsCollapsed((collapsed) => !collapsed);
    if (isMobile && !isCollapsed) mobileToggleRef.current?.focus();
  };

  /** Return to the dashboard and dismiss the mobile drawer even when already home. */
  const handleHomeClick = () => {
    void navigate('/admin');
    if (isMobile) {
      setIsCollapsed(true);
      mobileToggleRef.current?.focus();
    }
  };

  /**
   * Mobile top-bar action: the cog navigates into Settings, then on the
   * settings route the same button becomes a back arrow that pops history
   * (`navigate(-1)`) so the user resumes exactly where they were (e.g. a
   * half-built conversation) with no page reset. Falls back to the dashboard
   * only on a direct/deep entry with no in-app history to pop - React Router
   * tags that first entry with the sentinel key `'default'`.
   */
  const handleSettingsToggle = () => {
    if (!isSettingsRoute) {
      void navigate('/admin/settings');
    } else if (location.key === 'default') {
      void navigate('/admin');
    } else {
      void navigate(-1);
    }
  };

  // Handle clicking on backdrop (mobile overlay)
  const handleBackdropClick = () => {
    if (isMobile && !isCollapsed) {
      setIsCollapsed(true);
      mobileToggleRef.current?.focus();
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      void navigate('/login');
    } catch (error) {
      captureException(
        error instanceof Error ? error : new Error('Sign out failed'),
        'CollapsibleSidebar.handleSignOut',
        { userEmail: user?.email }
      );
      // Still navigate to login on error - user likely wants to leave
      void navigate('/login');
    }
  };

  const menuItems = [
    {
      label: 'Settings',
      icon: <IconSettings className="w-4 h-4" />,
      onClick: () => navigate('/admin/settings'),
    },
    // Billing is hidden for viewers/editors - they lack
    // `organization:view_billing`. BillingPage has a matching hard gate.
    ...(hasPermission('organization:view_billing')
      ? [{
        label: 'Billing',
        icon: <IconCreditCard className="w-4 h-4" />,
        onClick: () => navigate('/admin/billing'),
      }]
      : []),
    {
      label: 'Report Bug',
      icon: <IconBug className="w-4 h-4" />,
      onClick: () => navigate('/admin/settings?report=bug_report', { state: { scrollTo: 'support' } }),
    },
    {
      label: 'Log out',
      icon: <IconLogout className="w-4 h-4" />,
      onClick: handleSignOut,
    },
  ];

  const hasMultipleOrgs = userOrgs.length > 1;

  /** Org switcher entries + "Add new space". Used by the desktop combined
   *  dropdown and by the mobile "switch space" icon menu. */
  const spaceSwitchItems = [
    ...(hasMultipleOrgs ? userOrgs.map(org => {
      const isActive = org.id === activeOrg?.id;
      return {
        label: org.name,
        icon: isActive
          ? <IconCheck className="w-4 h-4 text-volt" />
          : <div className="w-4 h-4 rounded-lg bg-volt/20 text-volt-soft flex items-center justify-center text-xs font-bold">{org.name.charAt(0).toUpperCase()}</div>,
        onClick: (_e: React.MouseEvent) => {
          if (!isActive) setActiveOrg(org.id);
        },
        className: isActive ? 'text-volt-soft' : 'text-muted-foreground',
      };
    }) : []),
    {
      label: 'Add new space',
      icon: <PlusIcon className="w-4 h-4" />,
      onClick: (_e: React.MouseEvent) => {
        setCreateOrgModalOpen(true);
      },
      className: 'text-muted-foreground',
    },
  ];

  /** Account menu entries (Settings, Billing, Report Bug, Log out). Used by
   *  the mobile kebab-icon menu and as the tail of the desktop combined
   *  dropdown. */
  const accountMenuItems = menuItems.map(item => ({
    label: item.label,
    icon: item.icon,
    onClick: (_e: React.MouseEvent) => item.onClick?.(),
    className: item.label === 'Log out' ? 'text-danger' : 'text-muted-foreground',
  }));

  /**
   * Desktop dropdown - combines space switcher + account menu into one list.
   * Mobile splits these into two icon-triggered dropdowns.
   * Structure (top→bottom):
   *   1. Space switcher entries (orgs when >1, then "Add new space")
   *   2. Account menu (Settings, Billing, Report Bug, Log out)
   */
  const dropdownItems = [...spaceSwitchItems, ...accountMenuItems];

  const { planName, loading: planLoading } = usePlanBadge();
  const {
    isTrialing,
    daysRemaining,
    endsAt: trialEndsAt,
    shouldShowExtendButton,
    isEndingSoon,
  } = useTrialStatus();

  /**
   * Gate the whole trial UI on `organization:view_billing` so viewer/editor
   * roles don't see billing state leak into their sidebar.
   */
  const canViewBilling = hasPermission('organization:view_billing');
  const canManageBilling = hasPermission('organization:manage_billing');
  const showTrialBanner = isTrialing && canViewBilling;

  /** Plan-badge label: override "Pro" with "Pro Trial" while trialing so the
   *  badge itself communicates trial state - no separate countdown chip. */
  const planBadgeLabel = isTrialing && planName === 'Pro' ? 'Pro Trial' : planName;

  /** Org initial for the avatar display. */
  const orgInitial = activeOrg?.name?.charAt(0).toUpperCase() || '?';
  const orgName = activeOrg?.name || 'Organization';

  return (
    <>
      {/* Mobile Top Bar */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-background text-foreground h-16 flex items-center justify-between px-4 border-b border-border">
          {/* Hamburger/Close Menu Button */}
          <button
            ref={mobileToggleRef}
            onClick={handleToggleCollapse}
            className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
            aria-label="Toggle navigation menu"
            aria-expanded={!isCollapsed}
            aria-controls="admin-navigation"
          >
            <span className="relative block w-6 h-6" aria-hidden="true">
              <IconMenu2 className={clsx('absolute inset-0 w-6 h-6 transition-opacity duration-200 motion-reduce:transition-none', isCollapsed ? 'opacity-100' : 'opacity-0')} />
              <CloseIcon className={clsx('absolute inset-0 w-6 h-6 transition-opacity duration-200 motion-reduce:transition-none', isCollapsed ? 'opacity-0' : 'opacity-100')} />
            </span>
          </button>

          {/* Org Badge + Settings */}
          <div className="flex items-center gap-2">
            {activeOrg && (
              <div className="w-8 h-8 rounded-lg bg-volt/20 text-volt-soft flex items-center justify-center text-sm font-bold">
                {activeOrg.logo_url ? (
                  <img
                    src={activeOrg.logo_url}
                    alt={orgName}
                    className="w-full h-full rounded-lg object-cover"
                  />
                ) : (
                  orgInitial
                )}
              </div>
            )}
            <button
              onClick={handleSettingsToggle}
              className="relative p-2 rounded-lg hover:bg-surface-hover transition-colors"
              aria-label={isSettingsRoute ? 'Back' : 'Settings'}
            >
              {/* Both icons stay mounted and crossfade/rotate so the cog morphs
                  into the back arrow instead of hard-swapping. CSS can only tween a
                  persistent element, never a mount/unmount, so a conditional render
                  would always snap (DESIGN.md semantic-motion). Tailwind v4 compiles
                  rotate/scale to the standalone `rotate`/`scale` properties (not the
                  `transform` shorthand), so the transition list names them explicitly
                  - omitting them would let opacity fade while rotate/scale snapped. */}
              <span className="relative block w-5 h-5">
                <IconSettings
                  aria-hidden="true"
                  className={clsx(
                    'absolute inset-0 w-5 h-5 transition-[opacity,rotate,scale] duration-200 ease-out motion-reduce:transition-none',
                    isSettingsRoute
                      ? 'opacity-0 rotate-90 scale-50'
                      : 'opacity-100 rotate-0 scale-100',
                  )}
                />
                <IconArrowLeft
                  aria-hidden="true"
                  className={clsx(
                    'absolute inset-0 w-5 h-5 transition-[opacity,rotate,scale] duration-200 ease-out motion-reduce:transition-none',
                    isSettingsRoute
                      ? 'opacity-100 rotate-0 scale-100'
                      : 'opacity-0 -rotate-90 scale-50',
                  )}
                />
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Backdrop */}
      {isMobile && (
        <div className={clsx('fixed inset-0 transition-opacity duration-300 motion-reduce:transition-none', isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100')} style={{ zIndex: 50 }}>
          <div
            className="fixed inset-0 transition-opacity"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
            onClick={handleBackdropClick}
            aria-hidden="true"
          />
        </div>
      )}

      {/* Sidebar */}
      <div
        id="admin-navigation"
        inert={isMobile && isCollapsed}
        aria-hidden={(isMobile && isCollapsed) || undefined}
        className={clsx(
          "collapsible-sidebar shrink-0 bg-background text-foreground border-r border-border transition-[width,transform,translate] duration-300 ease-out motion-reduce:transition-none flex flex-col",
          // Desktop behavior - sticky to viewport while content scrolls
          !isMobile && "sticky top-0 h-screen",
          !isMobile && isCollapsed && "w-16",
          !isMobile && !isCollapsed && "w-72 max-w-72",
          // Mobile behavior - overlay slide from left
          isMobile && "fixed inset-y-0 left-0 h-full transform overflow-x-hidden",
          isMobile && isCollapsed && "-translate-x-full", // Hidden off-screen
          isMobile && !isCollapsed && "translate-x-0" // Visible
        )}
        style={isMobile ? {
          width: '80vw',
          maxWidth: '80vw',
          zIndex: 51, // Above everything including top bar (50)
        } : undefined}
        aria-modal={isMobile && !isCollapsed ? "true" : undefined}
        role={isMobile && !isCollapsed ? "dialog" : undefined}
        aria-label={isMobile && !isCollapsed ? "Navigation menu" : undefined}
      >
        {/* Header with Logo and Collapse Button - Desktop Only */}
        {!isMobile && (
          <div className="p-4 border-b border-border overflow-hidden">
            <div className="flex items-center gap-4">
              <button
                onClick={handleToggleCollapse}
                className="shrink-0 p-1 rounded-lg hover:bg-surface-hover transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt-soft"
                aria-label="Toggle navigation menu"
                aria-expanded={!isCollapsed}
                aria-controls="admin-navigation"
                title={isCollapsed ? 'Open menu' : 'Close menu'}
              >
                <span className="relative block w-5 h-5" aria-hidden="true">
                  <IconMenu2 className={clsx('absolute inset-0 w-5 h-5 transition-opacity duration-200 motion-reduce:transition-none', isCollapsed ? 'opacity-100' : 'opacity-0')} />
                  <IconChevronLeft className={clsx('absolute inset-0 w-5 h-5 transition-opacity duration-200 motion-reduce:transition-none', isCollapsed ? 'opacity-0' : 'opacity-100')} />
                </span>
              </button>
              <button
                onClick={() => navigate('/admin')}
                aria-label="holito home"
                aria-hidden={isCollapsed}
                inert={isCollapsed}
                className={clsx('flex shrink-0 items-center gap-2 transition-opacity duration-200 motion-reduce:transition-none cursor-pointer', isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100 hover:opacity-80')}
              >
                <img
                  src="/holito-logo-black.svg"
                  alt="holito"
                  className="h-6 w-auto dark:invert"
                />
                <span className="text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 bg-wash-accent text-foreground rounded-lg">
                  Beta
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Mobile Header with Logo and Close Button */}
        {isMobile && (
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <img
                  src="/holito-logo-black.svg"
                  alt="holito"
                  className="h-6 w-auto dark:invert"
                />
                <span className="text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 bg-wash-accent text-foreground rounded-lg">
                  Beta
                </span>
              </button>
              <button
                onClick={handleToggleCollapse}
                className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
                aria-label="Close navigation menu"
              >
                <CloseIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}

        {/* App Switcher */}
        <AppSwitcher isTextVisible={isTextVisible} />

        {/* Fixed Top Section - always render "Create" so the viewer
            knows they're in the right place. When they lack the
            `interviews:create` permission the button is disabled and a
            Tooltip explains why instead of replacing the control entirely. */}
        <div className="px-2 py-4 border-b border-border">
          <div className="space-y-1">
            <button
              type="button"
              onClick={handleHomeClick}
              aria-label="Home"
              aria-current={isDashboardRoute ? 'page' : undefined}
              title={isCollapsed ? 'Home' : undefined}
              className={clsx(
                'w-full text-left p-2 rounded-lg transition-colors motion-reduce:transition-none group flex items-center space-x-3 overflow-hidden cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt-soft',
                isDashboardRoute ? 'bg-surface text-foreground' : 'hover:bg-surface-hover',
              )}
            >
              <IconHome aria-hidden="true" className={clsx('w-5 h-5 shrink-0 transition-colors motion-reduce:transition-none', isDashboardRoute ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground')} />
              <span
                aria-hidden={isCollapsed}
                className={clsx('text-sm text-foreground whitespace-nowrap transition-opacity duration-200 motion-reduce:transition-none', isCollapsed ? 'opacity-0' : 'opacity-100')}
              >
                Home
              </span>
            </button>
            <Tooltip
              content={canCreateInterview ? '' : 'You have read-only access'}
              position="bottom"
            >
              <button
                onClick={() => {
                  // "Create" means start fresh: drop any in-session
                  // builder draft so the blank wizard isn't repopulated from a
                  // prior unfinished build. (The draft only exists to survive
                  // accidental navigation, not a deliberate new-conversation.)
                  clearSetupDraft();
                  captureEvent('conversation_creation.started', buildConversationCreationProps(activeWorkspace?.id, { source: 'sidebar' }));
                  if (recentSessions.length === 0) {
                    captureEvent('onboarding.first_create_clicked', buildOnboardingProps());
                  }
                  // If we're already on /admin/setup the user has clicked
                  // "Create" with intent to start over from a blank wizard
                  // - react-router would otherwise no-op on the same path
                  // and the in-progress step / fields / title would survive.
                  // We replace the entry with a fresh `resetKey` so
                  // AdminSetupPage can use it to remount CreateInterviewForm.
                  if (location.pathname === '/admin/setup') {
                    void navigate('/admin/setup', {
                      state: { resetKey: Date.now() },
                      replace: true,
                    });
                  } else {
                    void navigate('/admin/setup');
                  }
                }}
                disabled={!canCreateInterview}
                aria-label="Create"
                title={isCollapsed ? 'Create' : undefined}
                aria-disabled={!canCreateInterview || undefined}
                className={clsx(
                  "w-full text-left p-2 rounded-lg transition-colors group flex items-center space-x-3 overflow-hidden",
                  canCreateInterview && "cursor-pointer",
                  !canCreateInterview && "opacity-50 cursor-not-allowed",
                  canCreateInterview && location.pathname === '/admin/setup' && "bg-surface text-foreground",
                  canCreateInterview && location.pathname !== '/admin/setup' && "hover:bg-surface-hover",
                )}
              >
                <PlusIcon
                  className={clsx(
                    "w-5 h-5 shrink-0",
                    canCreateInterview && location.pathname === '/admin/setup'
                      ? "text-foreground"
                      : "text-muted-foreground",
                    canCreateInterview && "group-hover:text-foreground",
                  )}
                />
                <span
                  aria-hidden={isCollapsed}
                  className={clsx(
                    "text-sm whitespace-nowrap transition-opacity duration-200 motion-reduce:transition-none",
                    isCollapsed ? 'opacity-0' : 'opacity-100',
                    canCreateInterview && location.pathname === '/admin/setup'
                      ? "text-foreground"
                      : "text-foreground",
                    canCreateInterview && "group-hover:text-foreground",
                  )}
                >
                  Create
                </span>
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Workspace folder tree - replaces the old flat "Recent Interviews"
            list and the standalone WorkspaceSwitcher chip. Each workspace
            renders as a folder with a chevron toggle; the active workspace
            is auto-expanded. Hidden when the rail is collapsed so the
            64px-wide chrome does not flash incomplete folders. */}
        <div className="flex-1 overflow-hidden py-4 px-2">
          <div aria-hidden={isCollapsed} inert={isCollapsed} className={clsx('h-full transition-opacity duration-200 motion-reduce:transition-none', !isMobile && 'min-w-64', isCollapsed ? 'opacity-0' : 'opacity-100')}>
            <SidebarWorkspaceTree
              recentSessions={recentSessions}
              onSessionClick={onSessionClick}
              loading={loading}
              fetchError={fetchError}
              currentSessionId={currentSessionId}
            />
          </div>
        </div>

        {/* Trial banner - rendered below Recent Interviews and above the org
            profile so it has a clear home of its own. Desktop/mobile share
            the same component; visibility is gated on view_billing. */}
        <AnimatedPresence show={showTrialBanner && !isCollapsed} className="overflow-hidden">
          <TrialSidebarBanner
            daysRemaining={daysRemaining}
            endsAt={trialEndsAt}
            shouldShowExtendButton={shouldShowExtendButton}
            isEndingSoon={isEndingSoon}
            canManageBilling={canManageBilling}
          />
        </AnimatedPresence>

        {/* Org Info + Menu Section - Desktop (uses Headless UI DropdownMenu) */}
        {!isMobile && (
          <div className="px-2 py-4 border-t border-border">
            <DropdownMenu
              trigger={
                <button aria-label={`${orgName} account menu`} title={isCollapsed ? orgName : undefined} className="w-full flex items-center space-x-3 cursor-pointer hover:bg-surface-hover rounded-lg p-2 transition-colors overflow-hidden">
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-volt/20 text-volt-soft flex items-center justify-center text-sm font-bold">
                    {activeOrg?.logo_url ? (
                      <img
                        src={activeOrg.logo_url}
                        alt={orgName}
                        className="w-full h-full rounded-lg object-cover"
                      />
                    ) : (
                      orgInitial
                    )}
                  </div>
                  <div aria-hidden={isCollapsed} className={clsx('flex-1 min-w-0 text-left whitespace-nowrap transition-opacity duration-200 motion-reduce:transition-none', isCollapsed ? 'opacity-0' : 'opacity-100')}>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-foreground truncate" title={orgName}>
                        {orgName}
                      </span>
                      {hasMultipleOrgs && (
                        <IconSelector className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {!planLoading && planBadgeLabel && (
                        <span
                          data-testid="plan-badge"
                          className={clsx(
                            'text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-lg',
                            planName === 'Business' && 'bg-success text-success-foreground light:bg-success-soft light:text-white',
                            planName === 'Pro' && !isTrialing && 'bg-success text-success-foreground light:bg-success-soft light:text-white',
                            planName === 'Pro' && isTrialing && 'bg-warning text-warning-foreground',
                            planName === 'Free' && 'bg-muted-foreground text-background',
                          )}
                        >
                          {planBadgeLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              }
              items={dropdownItems}
              anchor="top start"
              width="w-64"
            />
          </div>
        )}

        {/* Org Info + Menu Section - Mobile
            Compact layout: the org row carries the avatar + name + plan
            on the left, and two stacked icon buttons on the right. Each icon
            opens its own DropdownMenu:
              • kebab  → Settings / Billing / Report Bug / Log out
              • switch → org list (when >1) + "Add new space"
            Panels portal via Headless UI anchor and need panelZIndex > 51 to
            sit above the mobile sidebar drawer. */}
        {isMobile && (
          <div className="border-t border-border px-4 py-3 flex items-center gap-3">
            <div className="shrink-0 w-8 h-8 rounded-lg bg-volt/20 text-volt-soft flex items-center justify-center text-sm font-bold">
              {activeOrg?.logo_url ? (
                <img
                  src={activeOrg.logo_url}
                  alt={orgName}
                  className="w-full h-full rounded-lg object-cover"
                />
              ) : (
                orgInitial
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-foreground truncate">{orgName}</div>
              <div className="flex items-center gap-2 mt-0.5">
                {!planLoading && planBadgeLabel && (
                  <span
                    data-testid="plan-badge-mobile"
                    className={clsx(
                      'text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-lg',
                      planName === 'Business' && 'bg-wash-accent text-foreground',
                      planName === 'Pro' && !isTrialing && 'bg-wash-success text-foreground',
                      planName === 'Pro' && isTrialing && 'bg-wash-warning text-foreground',
                      planName === 'Free' && 'bg-wash-neutral text-muted-foreground',
                    )}
                  >
                    {planBadgeLabel}
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0 flex flex-col gap-1">
              <DropdownMenu
                trigger={
                  <button
                    className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors cursor-pointer"
                    aria-label="Account menu"
                  >
                    <IconDotsVertical className="w-5 h-5 text-muted-foreground" />
                  </button>
                }
                items={accountMenuItems}
                anchor="top end"
                width="w-56"
                panelZIndex={60}
              />
              <DropdownMenu
                trigger={
                  <button
                    className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors cursor-pointer"
                    aria-label="Switch space"
                  >
                    <IconArrowsLeftRight className="w-5 h-5 text-muted-foreground" />
                  </button>
                }
                items={spaceSwitchItems}
                anchor="top end"
                width="w-56"
                panelZIndex={60}
              />
            </div>
          </div>
        )}
      </div>

      {/* No mobile profile menu - account info lives in Settings */}

      {/* Create new organization modal - triggered from dropdown */}
      <CreateOrgModal
        open={createOrgModalOpen}
        onClose={() => setCreateOrgModalOpen(false)}
      />
    </>
  );
};
